import { useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import styled from 'styled-components'
import iconDownloadWhite from '~/assets/icons-light-theme/download-icon-white.svg'
import iconCloseWhite from '~/assets/icons-light-theme/icon-close-white.svg'
import iconRotateWhite from '~/assets/icons-light-theme/rotate-icon-white.svg'
import { useModalState } from '~/atoms/ModalState'
import { FontStyle } from '~/components/core/Text'
import { light } from '~/constants/theme'
import { useWindowDimensions } from '~/hooks/useWindowDimensions'

import { ImageButton, Text } from './core'
import { ImageIcon } from './ImageIcon'

const Toolbar = styled.div`
  display: none;
  position: absolute;
  padding: 21px;
  bottom: 0;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  background: rgba(0, 0, 0, 0.4);
  border-top: none;
  border-radius: 0px 0px 8px 8px;
`

const CloseButtonWrapper = styled.div`
  display: flex;
  position: absolute;
  top: 20px;
  right: 20px;
`

export const ToolbarSection = styled.div`
  display: flex;
  height: 100%;
`

const StyledModal = styled(ReactModal)`
  box-sizing: border-box;
  overflow: hidden;
  outline: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover ${Toolbar} {
    display: flex;
  }
`

const ModalContentWrapper = styled.div<{ width: number; height: number }>`
  display: flex;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  border-radius: 8px;
  overflow: hidden;
  background: #545469;
`

const PreviewImage = styled.img<{ width: number; rotation: number }>`
  margin: auto;
  width: ${(props) => props.width}px;
  height: auto;
  transform: ${({ rotation }) => `rotate(${rotation}deg)`};
  image-orientation: from-image;
  user-select: none;
`

export const IconWrapper = styled.div`
  margin-left: 20px;
  display: flex;
`

const PreviewModal = () => {
  const { height, width } = useWindowDimensions()

  const {
    previewModal: [previewModalState, setPreviewModalState],
  } = useModalState()

  const { open, previewUrl, previewFilename } = previewModalState

  const [containerWidth, setContainerWidth] = useState(width * 0.5)
  const [containerHeight, setContainerHeight] = useState(width * 0.5)

  const [imgWidth, setImgWidth] = useState(0)

  const [rotation, setRotation] = useState(0)

  const handleCloseModal = () => {
    setRotation(0)
    setImgWidth(containerWidth)
    setPreviewModalState({ open: false })
  }

  useEffect(() => {
    handleCloseModal()
  }, [])

  const handleImageRender = ({ target: img }) => {
    const rate = img.offsetWidth / img.offsetHeight
    const scalingFactor = 1.2 // adjust this to change the size of the image
    const imgH =
      rate > 1
        ? (width * 0.514 * scalingFactor) / rate
        : height * 0.64 * scalingFactor
    const imgW =
      rate > 1
        ? width * 0.514 * scalingFactor
        : height * 0.64 * rate * scalingFactor
    setContainerHeight(imgH)
    if (imgH > imgW) setContainerWidth(imgH)
    else setContainerWidth(imgW)
    setImgWidth(imgW)
  }

  const handleDownload = async () => {
    if (!previewUrl || !previewFilename) {
      window.gtag('event', 'mms_preview_download_failed_missing_params', {})
    }

    window.gtag('event', 'mms_preview_downloaded', {})

    const img = await fetch(previewUrl!, {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache' },
    })
    const blob = await img.blob()
    const imgUrl = URL.createObjectURL(blob)

    const anchor = document.createElement('a')
    anchor.href = imgUrl
    anchor.download = previewFilename ?? ''

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    URL.revokeObjectURL(imgUrl)
  }

  const handleRotate = () => {
    window.gtag('event', 'mms_preview_rotated', {})
    if (containerHeight !== containerWidth) {
      if (imgWidth > containerHeight) {
        setImgWidth(containerHeight)
      } else {
        setImgWidth(containerWidth)
      }
    }
    setRotation(rotation + 90)
  }

  return (
    <StyledModal
      isOpen={open}
      ariaHideApp={false}
      onRequestClose={handleCloseModal}
      shouldCloseOnOverlayClick
      style={{ overlay: { backgroundColor: `${light.modalBackground}` } }}
    >
      <ModalContentWrapper
        width={containerWidth}
        height={containerHeight}
        onContextMenu={(e) => {
          e.preventDefault()
        }}
      >
        <PreviewImage
          width={imgWidth}
          src={previewUrl}
          onLoad={handleImageRender}
          rotation={rotation}
        />
      </ModalContentWrapper>
      <CloseButtonWrapper>
        <ImageButton
          icon={iconCloseWhite}
          width="38px"
          height="38px"
          imgWidth={20}
          imgHeight={20}
          backColor={light.dark900}
          hoveredColor={light.dark900}
          activeColor={light.dark900}
          onClick={handleCloseModal}
        />
      </CloseButtonWrapper>
      <Toolbar>
        <ToolbarSection>
          <ImageIcon
            icon={iconRotateWhite}
            onClick={handleRotate}
            width={20}
            height={20}
            cursor
            style={{ marginLeft: '20px' }}
          />
          <Text
            fontStyle={FontStyle.Normal}
            text="Rotate"
            color="white"
            style={{
              marginTop: '3px',
              marginLeft: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={handleRotate}
          />
        </ToolbarSection>
        <ToolbarSection>
          <ImageIcon
            icon={iconDownloadWhite}
            onClick={() => handleDownload()}
            width={20}
            height={20}
            cursor
          />
          <Text
            fontStyle={FontStyle.Normal}
            text="Download"
            color="white"
            style={{
              marginTop: '3px',
              marginLeft: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => handleDownload()}
          />
        </ToolbarSection>
      </Toolbar>
    </StyledModal>
  )
}

export default PreviewModal
